/**
 * Typed client for the /api/forecast endpoint.
 *
 * The shape comes from src/lib/types.ts which is generated against
 * api/forecast.js. If that handler's response shape changes, update the types
 * first — TypeScript will then surface every component that reads stale
 * fields.
 */

import { useQuery } from "@tanstack/react-query";
import type { ForecastResponse } from "./types";

export async function fetchForecast(): Promise<ForecastResponse> {
  const res = await fetch("/api/forecast");
  if (!res.ok) {
    throw new Error(`Forecast API error: ${res.status}`);
  }
  // Trust the API to return ForecastResponse — see types.ts for the contract.
  return res.json() as Promise<ForecastResponse>;
}

export const FORECAST_QUERY_KEY = ["forecast"] as const;

/**
 * Hook wrapping fetchForecast with TanStack Query. Stale time matches the
 * server-side cache TTL (10 min) so background refetches don't spam the API.
 */
export function useForecast() {
  return useQuery({
    queryKey: FORECAST_QUERY_KEY,
    queryFn: fetchForecast,
    staleTime: 10 * 60 * 1000,
  });
}
